import * as importedFormats from '@fds/wm-shared-formats';
import { ConsoleLogger } from '@fds/wm-typescript-logger';
import { Big } from 'big.js';
import { isAliasFormat, isExtendsNumberFormat, isRangeFormat } from './util/guards';
var NumberFormatter = /** @class */ (function () {
    function NumberFormatter(locale) {
        this.formats = {};
        this.fallbackFormats = {};
        this.logger = ConsoleLogger.getLogger(NumberFormatter.LOGGER_NAME);
        this.setLocale(locale);
        this.importFormats(importedFormats.number.formats);
        this.importFallbackFormats(importedFormats.number.fallback);
    }
    NumberFormatter.prototype.setLocale = function (locale) {
        this.locale = locale;
    };
    NumberFormatter.prototype.setFormat = function (locale, formatCode, format) {
        this.createFormatForNewLocale(locale, NumberFormatter.defaultDecimalPoint, NumberFormatter.defaultThousandsSeparator, NumberFormatter.defaultEmptyValue);
        this.formats[locale].formats[formatCode] = format;
    };
    NumberFormatter.prototype.importFormats = function (formats) {
        var _this = this;
        Object.keys(formats).forEach(function (locale) {
            var localeFormat = formats[locale];
            _this.createFormatForNewLocale(locale, localeFormat.decimalPoint, localeFormat.thousandsSeparator, localeFormat.emptyValue);
            Object.keys(formats[locale].formats)
                .forEach(function (code) { return _this.setFormat(locale, code, localeFormat.formats[code]); });
        });
    };
    NumberFormatter.prototype.importFallbackFormats = function (formats) {
        this.fallbackFormats = formats;
    };
    NumberFormatter.isEmptyValue = function (value) {
        return value === null || value === undefined || value === '';
    };
    NumberFormatter.isParseable = function (value) {
        return !isNaN(parseFloat(value)) && isFinite(value);
    };
    NumberFormatter.prototype.format = function (value, formatCode, locale) {
        var formatLocale = locale || this.locale;
        if (NumberFormatter.isEmptyValue(value)) {
            return this.getEmptyValue(formatLocale);
        }
        if (!NumberFormatter.isParseable(value)) {
            throw new Error("given value of '" + value + "' is not numeric");
        }
        var number = new Big(value);
        var format = this.getFormat(formatCode, formatLocale);
        var thousandsSeparator = this.getThousandsSeparator(formatLocale);
        var decimalPoint = this.getDecimalPoint(formatLocale);
        var formatDefinition = isRangeFormat(format) ? NumberFormatter.getDefinitionFromRange(format, value) : format;
        var formattedNumber = NumberFormatter.formatNumber(number, decimalPoint, thousandsSeparator, formatDefinition);
        var prefix = NumberFormatter.createPrefix(formatDefinition, number);
        var suffix = NumberFormatter.createSuffix(formatDefinition);
        return prefix + formattedNumber + suffix;
    };
    NumberFormatter.createPrefix = function (format, number) {
        return (format.signed && Number(number) > 0) ? '+' : '';
    };
    // according to DIN 5008 there's a space between number and percent sign.
    NumberFormatter.createSuffix = function (format) {
        var suffix = (format.percentSign) ? ' %' : '';
        return ('suffix' in format) ? suffix + format.suffix : suffix;
    };
    NumberFormatter.prototype.createFormatForNewLocale = function (locale, decimalPoint, thousandsSeparator, emptyValue) {
        if (!(locale in this.formats)) {
            this.formats[locale] = {
                decimalPoint: decimalPoint,
                thousandsSeparator: thousandsSeparator,
                emptyValue: emptyValue,
                formats: {},
            };
        }
    };
    NumberFormatter.prototype.getFormat = function (formatCode, locale) {
        var formats = this.formats[locale];
        var projectFormats = NumberFormatter.projectFormats[locale];
        var format = null;
        if (NumberFormatter.containsFormatCode(projectFormats, formatCode)) {
            format = projectFormats.formats[formatCode];
        }
        else if (NumberFormatter.containsFormatCode(formats, formatCode)) {
            format = formats.formats[formatCode];
        }
        else if (NumberFormatter.containsFormatCode(NumberFormatter.projectFallbackFormats, formatCode)) {
            format = NumberFormatter.projectFallbackFormats.formats[formatCode];
        }
        else if (NumberFormatter.containsFormatCode(this.fallbackFormats, formatCode)) {
            format = this.fallbackFormats.formats[formatCode];
        }
        else {
            throw new Error("format '" + formatCode + "' unknown for locale '" + locale + "'.");
        }
        if (isAliasFormat(format)) {
            return this.getFormat(format.alias, locale);
        }
        if (isExtendsNumberFormat(format)) {
            format = Object.assign({}, this.getFormat(format.extends, locale), format.by);
        }
        return format;
    };
    NumberFormatter.prototype.getPrioritizedFormats = function (locale) {
        var projectFormats = NumberFormatter.projectFormats[locale];
        var formats = this.formats[locale];
        var projectFallbackFormats = NumberFormatter.projectFallbackFormats;
        var fallbackFormats = this.fallbackFormats;
        var prioritizedFormats = [projectFormats, formats, projectFallbackFormats, fallbackFormats];
        return prioritizedFormats.filter(function (format) { return !!format; });
    };
    NumberFormatter.prototype.getEmptyValue = function (locale) {
        var prioritizedFormats = this.getPrioritizedFormats(locale);
        var emptyValueFormat = prioritizedFormats.find(function (format) { return 'emptyValue' in format; });
        return !!emptyValueFormat ? emptyValueFormat.emptyValue : NumberFormatter.defaultEmptyValue;
    };
    NumberFormatter.prototype.getThousandsSeparator = function (locale) {
        var prioritizedFormats = this.getPrioritizedFormats(locale);
        var thousandsSeparatorFormat = prioritizedFormats.find(function (format) { return 'thousandsSeparator' in format; });
        return !!thousandsSeparatorFormat ? thousandsSeparatorFormat.thousandsSeparator : NumberFormatter.defaultThousandsSeparator;
    };
    NumberFormatter.prototype.getDecimalPoint = function (locale) {
        var prioritizedFormats = this.getPrioritizedFormats(locale);
        var decimalPointFormat = prioritizedFormats.find(function (format) { return 'decimalPoint' in format; });
        return !!decimalPointFormat ? decimalPointFormat.decimalPoint : NumberFormatter.defaultDecimalPoint;
    };
    NumberFormatter.setProjectFormats = function (formats) {
        NumberFormatter.projectFormats = formats;
    };
    NumberFormatter.setProjectFallbackFormats = function (formats) {
        NumberFormatter.projectFallbackFormats = formats;
    };
    NumberFormatter.getDefinitionFromRange = function (format, number) {
        var value = Number(number);
        var range = format.range;
        for (var i = 0; i < range.length; i++) {
            if (value <= range[i].maxValue) {
                return range[i].definition;
            }
        }
        return range[range.length - 1].definition;
    };
    NumberFormatter.containsFormatCode = function (formatContainer, formatCode) {
        return !!formatContainer && 'formats' in formatContainer && formatCode in formatContainer.formats;
    };
    NumberFormatter.formatNumber = function (number, decimalPoint, thousandsSeparator, format) {
        var big = number;
        if (typeof format.factor === 'number' && format.factor !== 1) {
            big = big.times(format.factor);
        }
        // round INT values
        if (Math.max(format.decimals, format.relevantDecimals) === 0) {
            big = big.round();
        }
        big = NumberFormatter.adjustDecimals(big, format.decimals, format.relevantDecimals);
        var numberString = big.toFixed();
        if (numberString.indexOf('.') === -1) {
            numberString = big.toFixed(1);
        }
        var parts = numberString.split('.');
        var beforeDecimal = format.thousandsSeparator ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator) : parts[0];
        var afterDecimal = parts[1].substring(0, Math.max(format.decimals, format.relevantDecimals));
        // remove unwanted trailing zeros
        for (var i = 0; i < afterDecimal.length - format.decimals; i++) {
            if (afterDecimal.charAt(afterDecimal.length - 1) === '0') {
                afterDecimal = afterDecimal.slice(0, -1);
            }
            else {
                break;
            }
        }
        var missingZeros = Math.max(format.decimals - afterDecimal.length, 0);
        afterDecimal = afterDecimal + '0'.repeat(missingZeros);
        if (afterDecimal.length > 0) {
            return beforeDecimal + decimalPoint + afterDecimal;
        }
        return beforeDecimal;
    };
    NumberFormatter.adjustDecimals = function (number, decimals, relevantDecimals) {
        if (relevantDecimals > decimals && relevantDecimals > 0) {
            return new Big(number.toFixed(relevantDecimals));
        }
        if (decimals > 0) {
            return new Big(number.toFixed(decimals));
        }
        return number;
    };
    NumberFormatter.LOGGER_NAME = 'NumberFormatter';
    NumberFormatter.projectFormats = {};
    NumberFormatter.defaultDecimalPoint = '.';
    NumberFormatter.defaultThousandsSeparator = '';
    NumberFormatter.defaultEmptyValue = '-';
    return NumberFormatter;
}());
export { NumberFormatter };
//# sourceMappingURL=NumberFormatter.js.map